import React, { useEffect, useRef, useState } from "react";
import ayoba from "../../assets/images/ayoba.png";
import { HiArrowRight } from "react-icons/hi2";
import iosBadge from "../../assets/images/ios.png";
import playStoreBadge from "../../assets/images/goo.png";
import logo from "../../assets/images/md_logo.png";
import mcareLogo from "../../assets/images/mcarehd.png";
import mtnyellow from "../../assets/images/mtn.png";
import { getCurrentYear } from "../../utils/currentYear";

// --- Scroll & motion utilities (reusable, respects reduced motion) ---
const IN_VIEW_OPTIONS = {
  threshold: 0.08,
  rootMargin: "0px 0px -60px 0px",
} as const;
const MOTION_DURATION = "duration-700" as const;
const MOTION_EASING = "ease-out" as const;

function useInView(
  options: { threshold?: number; rootMargin?: string } = IN_VIEW_OPTIONS,
) {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsInView(true);
      },
      {
        threshold: options.threshold ?? IN_VIEW_OPTIONS.threshold,
        rootMargin: options.rootMargin ?? IN_VIEW_OPTIONS.rootMargin,
      },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [options.threshold, options.rootMargin]);

  return { ref, isInView };
}

function usePrefersReducedMotion() {
  const [prefersReduced, setPrefersReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReduced(mq.matches);
    const handler = () => setPrefersReduced(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return prefersReduced;
}
type AnimateDirection = "up" | "down" | "left" | "right" | "none";

const directionClasses: Record<
  AnimateDirection,
  { initial: string; inView: string }
> = {
  up: { initial: "translate-y-6", inView: "group-[.in-view]:translate-y-0" },
  down: { initial: "-translate-y-6", inView: "group-[.in-view]:translate-y-0" },
  left: { initial: "translate-x-6", inView: "group-[.in-view]:translate-x-0" },
  right: {
    initial: "-translate-x-6",
    inView: "group-[.in-view]:translate-x-0",
  },
  none: { initial: "", inView: "" },
};

function AnimateInView({
  children,
  direction = "up",
  staggerMs = 0,
  className = "",
}: {
  children: React.ReactNode;
  direction?: AnimateDirection;
  staggerMs?: number;
  className?: string;
}) {
  const { ref, isInView } = useInView();
  const prefersReduced = usePrefersReducedMotion();
  const { initial, inView } = directionClasses[direction];
  const showImmediately = prefersReduced;
  const visible = isInView || showImmediately;

  return (
    <div
      ref={ref}
      className={`group ${visible ? "in-view" : ""} ${showImmediately ? "reduce-motion" : ""} ${className}`.trim()}
    >
      <div
        className={`opacity-0 ${initial} transition-all ${MOTION_DURATION} ${MOTION_EASING} group-[.in-view]:opacity-100 ${inView} group-[.reduce-motion]:!opacity-100 group-[.reduce-motion]:!translate-y-0 group-[.reduce-motion]:!translate-x-0 group-[.reduce-motion]:!transition-none`}
        style={
          staggerMs > 0
            ? { transitionDelay: visible ? `${staggerMs}ms` : "0ms" }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  );
}

export default function Footer() {
  return (
    // <footer className="bg-gray-900 w-full">
    //   <div className="container px-6 py-8 mx-auto">
    //     <div className="flex flex-col items-center text-center">
    //       <img
    //         className="w-auto h-7"
    //         // src="https://merakiui.com/images/full-logo.svg"
    //         src={med}
    //         alt=""
    //       />

    //       <div className="flex flex-wrap justify-center mt-6 -mx-4">
    //         <Link
    //           to="/"
    //           className="mx-4 text-sm text-gray-300 hover:text-blue-400"
    //         >
    //           Home
    //         </Link>

    //         <Link
    //           to="/faq"
    //           className="mx-4 text-sm text-gray-300 hover:text-blue-400"
    //         >
    //           FAQs
    //         </Link>

    //         <Link
    //           to="/terms"
    //           className="mx-4 text-sm text-gray-300 hover:text-blue-400"
    //         >
    //           Terms and Conditions
    //         </Link>
    //         {/* <Link
    //           to="/contact-us"
    //           className="mx-4 text-sm text-gray-300 hover:text-blue-400"
    //         >
    //           Contact Us
    //         </Link> */}
    //       </div>
    //     </div>

    //     <hr className="my-6 border-gray-200 md:my-10 dark:border-gray-700" />

    //     <div className="flex flex-col items-center sm:flex-row sm:justify-between">
    //       <p className="text-sm text-gray-500 dark:text-gray-300">
    //         © 2024 Medpharma. All rights reserved
    //       </p>

    //       <div className="flex -mx-2">
    //         <img src={mymtn} alt="ios" className="w-9 h-5 fill-current mx-2 " />

    //         <img
    //           src={ayoba}
    //           alt="ios"
    //           className="w-14 h-5 fill-current mx-2 "
    //         />

    //         <img
    //           src={medpharma}
    //           alt="ios"
    //           className="w-14 h-5 fill-current mx-2 "
    //         />
    //       </div>
    //     </div>
    //   </div>
    // </footer>

    <>
      {/* CTA + Download — full-width, futuristic, with transitions */}
      <section
        id="get-started"
        className="mt-16 lg:mt-24 mb-0"
        aria-label="Get started and download the app"
      >
        <AnimateInView direction="up">
          <div className="w-screen relative left-1/2 -translate-x-1/2 max-w-none">
            <div className="relative overflow-hidden bg-gradient-to-br from-[#0c1929] via-[#1a2744] to-[#0f172a] text-white transition-all duration-500">
              {/* Ambient orbs */}
              <div
                className="absolute inset-0 pointer-events-none overflow-hidden"
                aria-hidden="true"
              >
                <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#1A94EC]/12 blur-[100px] transition-opacity duration-700" />
                <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-[#1A94EC]/10 blur-[80px] transition-opacity duration-700" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full bg-sky-500/5 blur-[60px] transition-opacity duration-700" />
              </div>
              {/* Mini circle designs - scattered dots and rings */}
              <div
                className="absolute inset-0 pointer-events-none overflow-hidden"
                aria-hidden="true"
              >
                <span className="absolute top-[12%] left-[8%] w-2 h-2 rounded-full bg-white/10" />
                <span className="absolute top-[18%] right-[15%] w-1.5 h-1.5 rounded-full bg-[#1A94EC]/30" />
                <span className="absolute top-[25%] left-[22%] w-1 h-1 rounded-full bg-white/20" />
                <span className="absolute top-[35%] right-[6%] w-3 h-3 rounded-full border border-white/10" />
                <span className="absolute top-[42%] left-[12%] w-2 h-2 rounded-full bg-sky-400/15" />
                <span className="absolute top-[55%] left-[5%] w-1.5 h-1.5 rounded-full bg-white/15" />
                <span className="absolute top-[60%] right-[22%] w-2.5 h-2.5 rounded-full border border-[#1A94EC]/20" />
                <span className="absolute top-[70%] left-[18%] w-1 h-1 rounded-full bg-white/10" />
                <span className="absolute top-[75%] right-[10%] w-2 h-2 rounded-full bg-[#1A94EC]/25" />
                <span className="absolute top-[85%] left-[25%] w-3 h-3 rounded-full border border-white/5" />
                <span className="absolute top-[88%] right-[28%] w-1.5 h-1.5 rounded-full bg-white/15" />
                <span className="absolute top-[15%] right-[35%] w-1 h-1 rounded-full bg-sky-300/20" />
                <span className="absolute top-[50%] right-[38%] w-2 h-2 rounded-full border border-white/8" />
                <span className="absolute top-[80%] left-[40%] w-1 h-1 rounded-full bg-white/12" />
              </div>
              {/* Top / bottom gradient fade */}
              <div
                className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/25 to-transparent pointer-events-none"
                aria-hidden="true"
              />
              <div
                className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/15 to-transparent pointer-events-none"
                aria-hidden="true"
              />

              <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-16 lg:py-20">
                <div className="text-center">
                  <p className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/90 text-xs sm:text-sm font-medium mb-6 sm:mb-8 transition-all duration-300 hover:bg-white/10 hover:border-white/15">
                    37K+ loyal and satisfied users
                  </p>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold tracking-tight mb-3 sm:mb-4 transition-all duration-300">
                    Ready to feel better every day?
                  </h2>
                  <p className="text-gray-400 text-sm sm:text-base lg:text-lg max-w-xl mx-auto mb-8 sm:mb-10 leading-relaxed transition-colors duration-300">
                    Join thousands on their wellness journey with Mcare. Start
                    free on web or get the app.
                  </p>
                  <a
                    href="/sign-up"
                    className="group inline-flex items-center justify-center gap-2 bg-[#1A94EC] text-white font-medium px-7 py-4 rounded-xl hover:bg-[#0d7bc4] hover:shadow-[0_0_40px_-8px_rgba(26,148,236,0.5)] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-[#1A94EC] focus:ring-offset-2 focus:ring-offset-[#0f172a] transition-all duration-300 ease-out min-h-[52px] touch-manipulation shadow-lg shadow-[#1A94EC]/20"
                    aria-label="Get started with Mcare for free"
                  >
                    Get started for free
                    <HiArrowRight
                      className="w-5 h-5 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </a>
                </div>
                <div
                  className="mt-16"
                  role="contentinfo"
                  aria-label="Site footer"
                >
                  <AnimateInView direction="up">
                    <div className="container mx-auto max-w-6xl">
                      <div className="lg:flex lg:gap-8">
                        <div className="w-full lg:w-2/5">
                          <div className="px-0 lg:px-2">
                            <a href="/" className="inline-block">
                              <img
                                src={mcareLogo}
                                alt="Mcare"
                                className="h-8 md:h-10 w-auto object-contain"
                              />
                            </a>
                            <p className="max-w-sm mt-2 text-white text-sm">
                              Stay connected with us for updates on new services
                              and health tips.
                            </p>
                            <div className="flex mt-6 gap-3 flex-wrap">
                              <a
                                href="/"
                                className="text-gray-400 hover:text-white transition-colors"
                                aria-label="MTN"
                              >
                                <img
                                  src={mtnyellow}
                                  alt="MTN"
                                  className="h-5 w-auto object-contain"
                                />
                              </a>
                              <a
                                href="/"
                                className="text-gray-400 hover:text-white transition-colors"
                                aria-label="Ayoba"
                              >
                                <img
                                  src={ayoba}
                                  alt="Ayoba"
                                  className="h-5 w-auto object-contain"
                                />
                              </a>
                              <a
                                href="/"
                                className="text-gray-400 hover:text-white transition-colors"
                                aria-label="Medpharma"
                              >
                                <img
                                  src={logo}
                                  alt="Medpharma"
                                  className="h-5 w-auto object-contain"
                                />
                              </a>
                            </div>
                          </div>
                        </div>
                        <div className="mt-8 lg:mt-0 lg:flex-1">
                          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
                            <div>
                              <h3 className="text-white uppercase text-sm font-bold tracking-wide">
                                Why choose us?
                              </h3>
                              <a
                                href="/faq"
                                className="block mt-2 text-sm text-white/90 hover:text-white hover:underline"
                              >
                                FAQs
                              </a>
                              <a
                                href="/terms"
                                className="block mt-2 text-sm text-white/90 hover:text-white hover:underline"
                              >
                                Terms and Conditions
                              </a>
                              <a
                                href="/"
                                className="block mt-2 text-sm text-white/90 hover:text-white hover:underline"
                              >
                                Home
                              </a>
                            </div>
                            <div>
                              <h3 className="text-white uppercase text-sm font-bold tracking-wide">
                                Products
                              </h3>
                              <a
                                href="/packages"
                                className="block mt-2 text-sm text-white/90 hover:text-white hover:underline"
                              >
                                Packages
                              </a>
                            </div>
                            <div>
                              <h3 className="text-white uppercase text-sm font-bold tracking-wide">
                                Contact
                              </h3>
                              <a
                                href="tel:+233303000448"
                                className="block mt-2 text-sm text-white/90 hover:text-white hover:underline"
                              >
                                +233 30 300 0448
                              </a>
                              <a
                                href="mailto:customer@medpharma.care"
                                className="block mt-2 text-sm text-white/90 hover:text-white hover:underline"
                              >
                                customer@medpharma.care
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AnimateInView>
                </div>

                <div className="mt-5 sm:mt-14 lg:mt-16 pt-6 border-t border-white/10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 sm:gap-8">
                    <div className="text-center sm:text-left">
                      <h3 className="text-base sm:text-lg font-semibold text-white mb-1 transition-colors duration-300">
                        Get the app
                      </h3>
                      <p className="text-sm text-gray-500 transition-colors duration-300">
                        Medpharma App — better experience on the go
                      </p>
                      <p className=" text-gray-500 transition-colors duration-300 text-sm">
                        © Mcare. All rights reserved 2024 - {getCurrentYear()}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center sm:justify-end gap-4 sm:gap-5">
                      <a
                        href="https://apps.apple.com/gh/app/medpharma-app/id1477732400"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/store inline-flex items-center justify-center min-h-[48px] px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 hover:shadow-lg hover:shadow-black/10 active:scale-[0.98] transition-all duration-300 ease-out touch-manipulation"
                        aria-label="Download on the App Store"
                      >
                        <img
                          src={iosBadge}
                          alt="Download on the App Store"
                          className="h-9 sm:h-10 w-auto object-contain transition-transform duration-300 group-hover/store:scale-105"
                        />
                      </a>
                      <a
                        href="https://play.google.com/store/search?q=medpharma&c=apps&hl=en"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/store inline-flex items-center justify-center min-h-[48px] px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 hover:shadow-lg hover:shadow-black/10 active:scale-[0.98] transition-all duration-300 ease-out touch-manipulation"
                        aria-label="Get it on Google Play"
                      >
                        <img
                          src={playStoreBadge}
                          alt="Get it on Google Play"
                          className="h-9 sm:h-10 w-auto object-contain transition-transform duration-300 group-hover/store:scale-105"
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimateInView>
      </section>

      {/* Footer - matches design: navy background, Mcare logo, columns, dynamic year */}
      {/* <footer className="" role="contentinfo" aria-label="Site footer">
                <AnimateInView direction="up">
                    <div className="container p-6 mx-auto max-w-6xl">
                        <div className="lg:flex lg:gap-8">
                            <div className="w-full lg:w-2/5">
                                <div className="px-0 lg:px-2">
                                    <a href="/" className="inline-block">
                                        <img
                                            src={mcareLogo}
                                            alt="Mcare"
                                            className="h-8 md:h-10 w-auto object-contain"
                                        />
                                    </a>
                                    <p className="max-w-sm mt-2 text-white text-sm">
                                        Stay connected with us for updates on new services and health tips.
                                    </p>
                                    <div className="flex mt-6 gap-3 flex-wrap">
                                        <a href="/" className="text-gray-400 hover:text-white transition-colors" aria-label="MTN">
                                            <img src={mtnyellow} alt="MTN" className="h-5 w-auto object-contain" />
                                        </a>
                                        <a href="/" className="text-gray-400 hover:text-white transition-colors" aria-label="Ayoba">
                                            <img src={ayoba} alt="Ayoba" className="h-5 w-auto object-contain" />
                                        </a>
                                        <a href="/" className="text-gray-400 hover:text-white transition-colors" aria-label="Medpharma">
                                            <img src={logo} alt="Medpharma" className="h-5 w-auto object-contain" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-8 lg:mt-0 lg:flex-1">
                                <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
                                    <div>
                                        <h3 className="text-white uppercase text-sm font-bold tracking-wide">Why choose us?</h3>
                                        <a href="/faq" className="block mt-2 text-sm text-white/90 hover:text-white hover:underline">FAQs</a>
                                        <a href="/terms" className="block mt-2 text-sm text-white/90 hover:text-white hover:underline">Terms and Conditions</a>
                                        <a href="/" className="block mt-2 text-sm text-white/90 hover:text-white hover:underline">Home</a>
                                    </div>
                                    <div>
                                        <h3 className="text-white uppercase text-sm font-bold tracking-wide">Products</h3>
                                        <a href="/packages" className="block mt-2 text-sm text-white/90 hover:text-white hover:underline">Packages</a>
                                    </div>
                                    <div>
                                        <h3 className="text-white uppercase text-sm font-bold tracking-wide">Contact</h3>
                                        <a href="tel:+233303000448" className="block mt-2 text-sm text-white/90 hover:text-white hover:underline">+233 30 300 0448</a>
                                        <a href="mailto:customer@medpharma.care" className="block mt-2 text-sm text-white/90 hover:text-white hover:underline">customer@medpharma.care</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <hr className="h-px my-6 bg-white/20 border-none" />
                        <p className="text-center text-white/80 text-sm">
                            © Medpharma. All rights reserved 2024 - {getCurrentYear()}
                        </p>
                    </div>
                </AnimateInView>
            </footer> */}
    </>
  );
}
